import { ConstrainedText, Icon, Tooltip } from '@scality/core-ui';
import type React from 'react';
import {
  mergeRefs,
  SelectChip,
  SelectChipClearButton,
  SelectFieldBox,
  SelectFieldText,
  SelectTriggerButton,
} from '../../../select';
import { ChipLabel, PlaceholderLabel } from './DeploymentSelector.styles';

/** What downshift and Floating UI hand whichever skin renders the trigger. */
export type TriggerWiring = {
  isOpen: boolean;
  /** The trigger must refuse interaction — nothing to offer, or a gate above it. */
  isRefusing: boolean;
  /** Why it is refusing, shown as a tooltip. */
  refusalHint?: string;
  toggleProps: React.ComponentPropsWithoutRef<'button'>;
  toggleRef: React.Ref<HTMLButtonElement>;
  setReference: (node: HTMLElement | null) => void;
  /** How the selection is named, or null when nothing is held. */
  selectionLabel: string | null;
  onClear: () => void;
};

/** The composer's skin: a pill naming the selection, or a placeholder inviting one. */
export const ChipTrigger: React.FC<
  TriggerWiring & {
    placeholder: string;
    compact: boolean;
    icon?: string;
  }
> = ({
  isOpen,
  isRefusing,
  refusalHint,
  toggleProps,
  toggleRef,
  setReference,
  selectionLabel,
  onClear,
  placeholder,
  compact,
  icon,
}) =>
  selectionLabel !== null ? (
    <SelectChip ref={setReference}>
      <SelectTriggerButton type="button" ref={toggleRef} {...toggleProps}>
        {icon && <Icon name={icon} size="xs" />}
        {/* ConstrainedText owns both the truncation and its own tooltip, so the chip
            no longer hand-rolls either — `account - name - PRODUCT` overflows easily. */}
        <ChipLabel $compact={compact}>
          <ConstrainedText text={selectionLabel} lineClamp={1} />
        </ChipLabel>
      </SelectTriggerButton>
      <SelectChipClearButton label={`Clear ${selectionLabel}`} onClear={onClear} />
    </SelectChip>
  ) : (
    <Tooltip overlay={refusalHint}>
      <SelectTriggerButton
        type="button"
        ref={mergeRefs<HTMLButtonElement>(setReference, toggleRef)}
        {...toggleProps}
        aria-disabled={isRefusing}
        $isOpen={isOpen}
      >
        {icon && <Icon name={icon} size="xs" />}
        <PlaceholderLabel $compact={compact} variant={compact ? 'Small' : 'Basic'}>
          {placeholder}
        </PlaceholderLabel>
        <Icon name={'ArrowsUpDown'} size="xs" />
      </SelectTriggerButton>
    </Tooltip>
  );

/**
 * The dashboard filter bar's skin. The box holds the trigger rather than being it, so a
 * clear button can sit beside it.
 */
export const FieldTrigger: React.FC<TriggerWiring & { labelId: string; text: string }> = ({
  isOpen,
  isRefusing,
  refusalHint,
  toggleProps,
  toggleRef,
  setReference,
  selectionLabel,
  onClear,
  labelId,
  text,
}) => (
  <Tooltip overlay={refusalHint}>
    <SelectFieldBox ref={setReference} $isOpen={isOpen} $disabled={isRefusing}>
      <SelectTriggerButton
        type="button"
        ref={toggleRef}
        {...toggleProps}
        aria-labelledby={labelId}
        aria-disabled={isRefusing || undefined}
      >
        <SelectFieldText>{text}</SelectFieldText>
        <Icon name="ArrowsUpDown" size="xs" />
      </SelectTriggerButton>
      {selectionLabel !== null && <SelectChipClearButton label={`Clear ${selectionLabel}`} onClear={onClear} />}
    </SelectFieldBox>
  </Tooltip>
);
